// src/utils/branding/imageSignature.ts
/**
 * @file Magic-byte sniffing for the clinic branding uploads (S12, ClickUp 86d3w1g8u).
 * @module utils/branding/imageSignature
 *
 * *** WHY THIS EXISTS ***
 *
 * S9's `validateBrandingFile` checked the file's EXTENSION while the server checks its
 * CONTENT. A HEIC straight off an iPhone is routinely named `photo.jpg`, so it passed the
 * browser check, uploaded, and came back with
 * `"must be a file of type: png, jpg, jpeg, webp, ico"` — while being named `.jpg`. The
 * administrator has no way to act on that.
 *
 * So the browser now reads the first {@link SIGNATURE_LENGTH} bytes and identifies the file
 * the same way the server does. Two consequences, both deliberate:
 *
 *  - A HEIC/HEIF/AVIF named `.jpg` **passes** — those three are accepted platform-wide now
 *    and the FILE service transcodes them to PNG (S12 track A). The browser only has to
 *    accept them and identify them correctly.
 *  - A genuinely unsupported file is refused with a message naming **what was actually
 *    detected**, not what it was named.
 *
 * ⚠️ **SVG STAYS REJECTED (decision D10)** — it is an active-content format on an
 * admin-writable path. Renaming one to `.png` does not get it past this module.
 *
 * ⚠️ This is a MESSAGE-QUALITY check, not a security control. The server re-sniffs every
 * upload; nothing here is trusted by anything.
 */

/**
 * Every format this module can name. The first seven are accepted; the rest exist only so
 * a rejection can say what the file actually was.
 */
export type ImageFormat =
	| 'png'
	| 'jpeg'
	| 'webp'
	| 'ico'
	| 'heic'
	| 'heif'
	| 'avif'
	| 'gif'
	| 'bmp'
	| 'tiff'
	| 'svg'
	| 'pdf';

/**
 * THE CROSS-REPO CONTRACT (S12) — auth's edge rule, the file service's transcoder and this
 * list are one set. Do not extend it on one side only.
 */
export const ACCEPTED_FORMATS: readonly ImageFormat[] = ['png', 'jpeg', 'webp', 'ico', 'heic', 'heif', 'avif'];

/** Accepted file-name extensions → the format that extension claims. */
export const EXTENSION_FORMATS: Readonly<Record<string, ImageFormat>> = {
	png: 'png',
	jpg: 'jpeg',
	jpeg: 'jpeg',
	webp: 'webp',
	ico: 'ico',
	heic: 'heic',
	heif: 'heif',
	avif: 'avif',
};

/** Short name, for the "…, not a JPEG." half of a mismatch message. */
export const FORMAT_LABEL: Readonly<Record<ImageFormat, string>> = {
	png: 'PNG',
	jpeg: 'JPEG',
	webp: 'WEBP',
	ico: 'ICO',
	heic: 'HEIC',
	heif: 'HEIF',
	avif: 'AVIF',
	gif: 'GIF',
	bmp: 'BMP',
	tiff: 'TIFF',
	svg: 'SVG',
	pdf: 'PDF',
};

/** Article + noun, for the "“x.png” is an SVG image" half. */
export const FORMAT_DESCRIPTION: Readonly<Record<ImageFormat, string>> = {
	png: 'a PNG image',
	jpeg: 'a JPEG image',
	webp: 'a WEBP image',
	ico: 'an ICO icon',
	heic: 'a HEIC image',
	heif: 'a HEIF image',
	avif: 'an AVIF image',
	gif: 'a GIF image',
	bmp: 'a BMP image',
	tiff: 'a TIFF image',
	svg: 'an SVG image',
	pdf: 'a PDF document',
};

/**
 * Enough for every signature below. The ISO-BMFF brand is the deepest — `ftyp` at byte 4 and
 * the brand at bytes 8..12 — and 16 also covers `<?xml version=`.
 */
export const SIGNATURE_LENGTH = 16;

/**
 * ISO-BMFF `ftyp` major brands. HEIC, HEIF and AVIF are all the same container, told apart
 * only by this four-character code — which is why a `.heic` cannot be identified by any
 * fixed byte prefix the way a PNG can.
 *
 * ⚠️ **THIS LIST IS CLOSED, AND EVERY BRAND IN IT WAS CHECKED AGAINST libmagic.** The auth
 * edge and the file service both sniff with fileinfo/libmagic, so a brand this module accepts
 * that libmagic calls something else is precisely the client/server disagreement S12 exists
 * to remove. Verified on libmagic 5.46, which is what both services carry:
 *
 * | brand | libmagic says | in the contract's eight? |
 * |---|---|---|
 * | `heic`, `heix` | `image/heic` | yes |
 * | `mif1`, `heim` | `image/heif` | yes |
 * | `avif`, `avis` | `image/avif` | yes |
 * | `hevc`, `hevx` | `image/heic-sequence` | **NO** |
 * | `msf1` | `image/heif-sequence` | **NO** |
 *
 * The three SEQUENCE brands are deliberately absent: the `-sequence` MIME types are not among
 * the contract's eight and fail closed at the auth edge, so accepting one here would let a
 * file through the browser that the server then refuses. **Do not add them.** `avis` looks
 * like a sequence brand and is not treated as one by libmagic — it is checked, not assumed.
 */
const FTYP_BRANDS: Readonly<Record<string, ImageFormat>> = {
	heic: 'heic',
	heix: 'heic',
	mif1: 'heif',
	heim: 'heif',
	avif: 'avif',
	avis: 'avif',
};

const bytesOf = (text: string): number[] => Array.from(text, (character) => character.charCodeAt(0));

const matches = (bytes: Uint8Array, signature: number[], offset = 0): boolean =>
	bytes.length >= offset + signature.length && signature.every((byte, index) => bytes[offset + index] === byte);

const asciiAt = (bytes: Uint8Array, offset: number, length: number): string =>
	bytes.length >= offset + length ? String.fromCharCode(...bytes.subarray(offset, offset + length)) : '';

export const isAcceptedFormat = (format: ImageFormat | null): boolean =>
	format !== null && ACCEPTED_FORMATS.includes(format);

/** The format a file NAME claims, or `null` — only the accepted extensions are mapped. */
export const extensionFormat = (fileName: string): ImageFormat | null => {
	const extension = fileName.split('.').pop()?.toLowerCase() ?? '';
	return EXTENSION_FORMATS[extension] ?? null;
};

/**
 * Identify a file from its leading bytes. `null` means "nothing recognised" — which is a
 * rejection, so an unknown format never slips through by being unknown.
 */
export const identifyImageBytes = (bytes: Uint8Array): ImageFormat | null => {
	// PNG — the 4 bytes named in the S12 contract (the full signature continues 0D 0A 1A 0A).
	if (matches(bytes, [0x89, 0x50, 0x4e, 0x47])) return 'png';

	// JPEG — SOI + the first marker. Covers baseline, progressive, JFIF and EXIF alike.
	if (matches(bytes, [0xff, 0xd8, 0xff])) return 'jpeg';

	// WEBP — a RIFF container whose form type at byte 8 is `WEBP`.
	if (matches(bytes, bytesOf('RIFF')) && matches(bytes, bytesOf('WEBP'), 8)) return 'webp';

	// HEIC / HEIF / AVIF — one container, told apart by the major brand at bytes 8..12.
	//
	// ⚠️ Checked BEFORE the ICO signature, and that order is load-bearing: an ISO-BMFF file
	// opens with the `ftyp` box's 4-byte SIZE, and a 256-byte box is the bytes `00 00 01 00` —
	// byte-for-byte an ICO header. This match is 8 bytes deep, so it wins on the collision.
	if (matches(bytes, bytesOf('ftyp'), 4)) {
		const brand = asciiAt(bytes, 8, 4).toLowerCase();
		if (FTYP_BRANDS[brand]) return FTYP_BRANDS[brand];
	}

	// ICO — reserved 0x0000, then image type 1 (type 2 is a .cur cursor), then a plausible
	// image count. The count is only checked to keep this 4-byte signature from claiming
	// unrelated files that happen to start with two zero bytes.
	//
	// ⚠️ Identified by SIGNATURE, never by a MIME string. A valid `.ico` reports at least
	// three different MIME types depending on the sniffer and the browser —
	// `image/vnd.microsoft.icon`, `image/x-icon`, or nothing at all — so any code that keyed
	// on one of those spellings would reject two thirds of real icons.
	if (matches(bytes, [0x00, 0x00, 0x01, 0x00])) {
		const count = bytes.length >= 6 ? bytes[4] | (bytes[5] << 8) : 0;
		if (count > 0) return 'ico';
	}

	// ── Below here: recognised only so a rejection can name the format. ──

	if (matches(bytes, bytesOf('GIF8'))) return 'gif';
	if (matches(bytes, bytesOf('%PDF-'))) return 'pdf';
	if (matches(bytes, [0x49, 0x49, 0x2a, 0x00]) || matches(bytes, [0x4d, 0x4d, 0x00, 0x2a])) return 'tiff';
	if (matches(bytes, bytesOf('BM'))) return 'bmp';

	// SVG is text, so it is sniffed as text — after a UTF-8 BOM and any leading whitespace.
	// `<?xml` is reported as SVG rather than "some XML": an XML file dropped on a logo slot is
	// an SVG in every case that matters, and D10 rejects it either way.
	const start = matches(bytes, [0xef, 0xbb, 0xbf]) ? 3 : 0;
	const text = String.fromCharCode(...bytes.subarray(start)).replace(/^\s+/, '').toLowerCase();
	if (text.startsWith('<svg') || text.startsWith('<?xml') || text.startsWith('<!doctype svg')) return 'svg';

	return null;
};

/**
 * Read the leading bytes of a file.
 *
 * `File.slice()` keeps this to {@link SIGNATURE_LENGTH} bytes however large the file is —
 * a 30 MB photo is never pulled into memory to be told it is 30 MB.
 */
export const readSignatureBytes = async (file: File): Promise<Uint8Array> =>
	new Uint8Array(await file.slice(0, SIGNATURE_LENGTH).arrayBuffer());

/** Read a file's leading bytes and identify it. Rejects if the file cannot be read. */
export const detectImageFormat = async (file: File): Promise<ImageFormat | null> =>
	identifyImageBytes(await readSignatureBytes(file));
